import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { UDCalendar } from 'ud-form/components';
import { UDCalendarDayBadge } from 'ud-form/components/datetime/component';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import { CalendarGame } from 'modules/schedule/domain/interfaces/calendar';
import { fromMonthKey, getVisibleMonths, toDayKey, toMonthKey } from 'modules/schedule/domain/utils/calendar-months';
import { useSchedule } from 'modules/schedule/ui/hooks/use-schedule';

interface CalendarStripProps {
  /** Матчи по дням `yyyy-MM-dd` — по ним день получает отметку «есть матчи». */
  matchesByDay: Record<string, CalendarGame[]>;
  /** Месяцы `yyyy-MM`, матчи которых не загрузились. */
  failedMonths: string[];
  /** Идёт загрузка какого-то из месяцев. */
  isLoading: boolean;
  /** Повторить загрузку месяца по его ключу. */
  onRetry: (monthKey: string) => Promise<unknown>;
  /** Фильтр турниров: стоит слева в шапке, напротив листания. */
  filter?: React.ReactNode;
}

/**
 * Календарь расписания на всю ширину страницы: два месяца рядом, как в календарях ОС, — это `UDCalendar` кита
 * (`embedded`), а не своя сетка. Стрелки листают сразу на столько месяцев, сколько показано; «Сегодня» возвращает к
 * текущему месяцу и выбирает сегодняшний день. Переход стрелкой клавиатуры за край окна приходит в `onShownMonthChange`
 * (первый показанный месяц; клик по дню второго месяца окно не двигает).
 *
 * Матчи приходят снаружи (RTK Query, по месяцу на запрос): день с матчами отмечается точкой, как только его месяц
 * загружен. Упавший месяц — строка над календарём с «Повторить»: сетки у каждого месяца своей карточки больше нет.
 */
function CalendarStrip({ matchesByDay, failedMonths, isLoading, onRetry, filter }: CalendarStripProps) {
  const { t, i18n } = useLingui();
  const { currentDate, selectedDate, pickDay, showMonth, showPrevious, showNext, showToday } = useSchedule();

  // Листание кнопками пересоздаёт календарь (`key`) и ставит сторону появления; клавиатурный переход на соседний месяц
  // пересоздания не вызывает — иначе фокус терялся бы.
  const [paging, setPaging] = useState<{ key: number; from?: 'left' | 'right' }>({ key: 0 });
  const pageBy = useCallback((from: 'left' | 'right', action: () => void) => {
    setPaging((prev) => ({ key: prev.key + 1, from }));
    action();
  }, []);
  const handlePrevious = useCallback(() => pageBy('left', showPrevious), [pageBy, showPrevious]);
  const handleNext = useCallback(() => pageBy('right', showNext), [pageBy, showNext]);
  const handleToday = useCallback(() => {
    const today = new Date();
    const offset = (today.getFullYear() - currentDate.year) * 12 + today.getMonth() - currentDate.month;
    // Месяц не меняется — календарь не пересоздаётся и не анимируется, меняется только выбранный день.
    if (offset === 0) showToday();
    else pageBy(offset > 0 ? 'right' : 'left', showToday);
  }, [currentDate, pageBy, showToday]);

  const openToDate = useMemo(() => new Date(currentDate.year, currentDate.month, 1), [currentDate]);
  const selected = useMemo(() => new Date(selectedDate), [selectedDate]);

  const visibleFailedMonths = useMemo(() => {
    const visible = getVisibleMonths(currentDate).map(toMonthKey);
    return failedMonths.filter((key) => visible.includes(key));
  }, [currentDate, failedMonths]);

  const isMarked = useCallback((date: Date) => (matchesByDay[toDayKey(date)]?.length ?? 0) > 0, [matchesByDay]);

  // Под числом дня — сколько матчей (одна цифра: две в ячейке в 40 px не читаются). У прошедшего дня, где есть матчи без
  // результата, цифра тоном «ожидает решения»: это то, что организатору нужно найти в календаре. Смысл — и в подсказке (WCAG 1.4.1).
  const dayBadge = useCallback(
    (date: Date): UDCalendarDayBadge | null => {
      const games = matchesByDay[toDayKey(date)];
      if (!games?.length) return null;

      const isPast = toDayKey(date) < toDayKey(new Date());
      const withoutResult = isPast ? games.filter((game) => !game.hasResults).length : 0;
      const matchesLabel = plural(games.length, { one: '# матч', few: '# матча', many: '# матчей', other: '# матча' });

      return {
        text: String(games.length),
        tone: withoutResult > 0 ? 'pending' : 'neutral',
        title: withoutResult > 0 ? `${matchesLabel}, ${t`без результата: ${withoutResult}`}` : matchesLabel,
      };
    },
    [matchesByDay, t],
  );

  const handleMonthChange = useCallback(
    (date: Date) => {
      if (date.getMonth() !== currentDate.month || date.getFullYear() !== currentDate.year) {
        showMonth({ month: date.getMonth(), year: date.getFullYear() });
      }
    },
    [currentDate, showMonth],
  );

  return (
    <UDCard as="section" padding="lg" className="flex w-full flex-col gap-4" aria-busy={isLoading} data-testid="calendar-strip">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">{filter}</div>
        <div className="ml-auto flex items-center gap-3">
          {isLoading && <ClipLoader size={18} />}
          <div className="flex items-center gap-2">
            <UDIconButton
              icon={<ChevronLeft size={18} />}
              tooltip={t`Предыдущие месяцы`}
              aria-label={t`Предыдущие месяцы`}
              onClick={handlePrevious}
            />
            <UDButton variant="secondary" onClick={handleToday}>
              {t`Сегодня`}
            </UDButton>
            <UDIconButton
              icon={<ChevronRight size={18} />}
              tooltip={t`Следующие месяцы`}
              aria-label={t`Следующие месяцы`}
              onClick={handleNext}
            />
          </div>
        </div>
      </div>

      {visibleFailedMonths.map((key) => {
        const { month, year } = fromMonthKey(key);
        const monthLabel = `${new Date(year, month, 1).toLocaleString(i18n.locale, { month: 'long' })} ${year}`;

        return (
          <div key={key} className="flex items-center justify-between gap-3" role="alert" data-testid="calendar-month-failed">
            <UDText variant="text-2" as="span" className="!text-inkMuted">
              {t`Не удалось загрузить матчи: ${monthLabel}`}
            </UDText>
            <UDButton variant="secondary" onClick={() => onRetry(key)}>
              {t`Повторить`}
            </UDButton>
          </div>
        );
      })}

      <UDCalendar
        key={paging.key}
        enterFrom={paging.from}
        embedded
        monthsShown={2}
        selected={selected}
        onChange={pickDay}
        openToDate={openToDate}
        onShownMonthChange={handleMonthChange}
        isMarked={isMarked}
        dayBadge={dayBadge}
      />
    </UDCard>
  );
}

export default CalendarStrip;
